import styles from './home.module.css';
import { Photo } from '../../../components/photo';
import { Label } from '../../../components/label';
import { ProgressBar } from '../../../components/progress-bar';
import { ProgressPill } from '../progress-pill';
import { Card } from '../../../components/card';
import { Button } from '../../../components/button';
import { PageHead } from '../../../components/page-head';
// student-home.tsx — Student home view (elite-purple).
// Next gameplay session banner + welcome/rank row + campaigns grid.
import { useTranslation } from 'react-i18next';
import { useStudentProgress } from '../../../api/student/progress/hooks';
import { useStudentCampaigns } from '../../../api/student/campaigns/hooks';
import { campaignStatus, homeCampaigns, homeEmpty, type HomeEmpty } from '../../../api/student/campaigns/gating';
import { percentOf } from '../../../utils/percent';
import type { StudentCampaignSummary } from '../../../api/student/campaigns/types';
import type { StudentView } from '../student-dashboard';
import type { StudentNavigate } from '../student-location';
import { NextSessionBanner } from '../next-session-banner';
import { xpFraction } from '../xp-fraction';

interface StudentHomeProps {
  userName: string;
  firstName: string;
  onNavigate: (v: StudentView) => void;
  onOpenCampaign: (id: string) => void;
  /** Deep navigation (used by the next-gameplay banner). */
  navigate: StudentNavigate;
}

/** With no campaign to show, what is true of the student's (homeEmpty), in words: its key, written whole. */
const EMPTY_KEY: Record<HomeEmpty, string> = {
  none: 'student.campaigns.empty',
  allCompleted: 'student.home.allCampaignsDone',
  notOpen: 'student.home.campaignsNotOpen',
};

export function StudentHome({ userName, firstName: firstNameProp, onNavigate, onOpenCampaign, navigate }: StudentHomeProps) {
  const { t } = useTranslation();
  const firstName = firstNameProp || userName.split(' ')[0];

  const progress = useStudentProgress().data ?? null;
  const campaigns: StudentCampaignSummary[] = useStudentCampaigns().data ?? [];

  const level = progress?.level ?? 1;
  const xpPoints = progress?.xpPoints ?? 0;
  const nextLevelXp = progress?.nextLevelXp ?? null;
  const levelProgress = progress?.levelProgress ?? 0;
  const xpLabel = nextLevelXp !== null ? xpFraction(xpPoints, nextLevelXp) : `${xpPoints} XP`;

  // The current campaigns first, then the ended ones with work left (homeCampaigns, gating.ts).
  const topCampaigns = homeCampaigns(campaigns);

  return (
    <div className="page">
      <NextSessionBanner navigate={navigate} />

      <section className={`${styles.homeTop}`}>
        <Card as="header" framed padded={false} className={`${styles.welcome} reveal`}>
          <Label>{t('student.home.standingLabel')}</Label>
          <h1 className={`${styles.welcome__hi}`}>
            {t('student.home.welcome', { name: firstName })} <span aria-hidden="true">👋</span>
          </h1>
          <p className={`${styles.welcome__sub}`}>{t('student.home.subtitle')}</p>
        </Card>

        <Card framed padded={false} className={`${styles.rankcard} reveal`}>
          <div className={`${styles.rankcard__ring}`}><span className={`${styles.rankcard__lvl}`}>{level}</span></div>
          <div className={`${styles.rankcard__role}`}>{t('student.home.totalXp')}</div>
          <div className={`${styles.rankcard__xp}`}>{xpLabel}</div>
          <ProgressBar value={levelProgress * 100} grow gold label={xpLabel} />
        </Card>
      </section>

      <PageHead level="section" action={<Button variant="link" onClick={() => onNavigate('campaigns')}>
          {t('student.home.viewAll')} →
        </Button>}>{t('student.home.myCampaigns')}</PageHead>

      {topCampaigns.length === 0 ? (
        <p className={`${styles.homeEmpty}`}>{t(EMPTY_KEY[homeEmpty(campaigns)])}</p>
      ) : (
        <div className={`${styles.grid3}`}>
          {topCampaigns.map((c) => {
            const pct = percentOf(c.xpEarned, c.totalXp);
            const status = campaignStatus(c);
            return (
              <Card key={c.id} padded={false} className={`${styles.cc} reveal`} onClick={() => onOpenCampaign(c.id)}>
                <Photo className={`${styles.cc__art}`} src={c.imageUrl}>
                  <ProgressPill of="campaign" status={status} />
                </Photo>
                <div className={`${styles.cc__body}`}>
                  <span className={`${styles.cc__co}`}>{c.gameName}</span>
                  <h3 className={`${styles.cc__title}`}>{c.name}</h3>
                  <ProgressBar value={pct} grow gold={status === 'completed'} />
                  <div className={`${styles.cc__foot}`}>
                    <span>{xpFraction(c.xpEarned, c.totalXp)}</span>
                    <span className={`${styles.cc__pct}`}>{pct}%</span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
